import {
	BasicQueryStringUtils,
	QueryStringUtils,
} from '@openid/appauth/built/query_string_utils';
import {
	TokenRequest,
	GRANT_TYPE_REFRESH_TOKEN,
} from '@openid/appauth/built/token_request';
import {
	TokenErrorJson,
	TokenError,
	TokenResponse,
	TokenResponseJson,
} from '@openid/appauth/built/token_response';
import { RevokeTokenRequest } from '@openid/appauth/built/revoke_token_request';
import DSAuth from '../dsAuth/DSAuth';
import { stringConstants, statusConstants } from '../AuthConstants';
import { logConstants } from '../../utils/Constants';
import { Logger } from '../Logger';
import { SentryLogger } from '../../sentryLogging/SentryLogger';
import { HttpRequestError } from '../../utils/http/HttpErrors';
import * as Sentry from '@sentry/browser';

export default class OIDCTokenRequestHandler {
	private static instance: OIDCTokenRequestHandler;
	static getInstance(): OIDCTokenRequestHandler {
		if (!OIDCTokenRequestHandler.instance) {
			OIDCTokenRequestHandler.instance = new OIDCTokenRequestHandler();
		}
		return OIDCTokenRequestHandler.instance;
	}

	private constructor(
		public readonly utils: QueryStringUtils = new BasicQueryStringUtils()
	) {}

	/**Checks whether the parameter is a successful response or error based on presence
	 * of the error field in it
	 * @param response response object
	 */
	private isTokenResponse(
		response: TokenResponseJson | TokenErrorJson
	): response is TokenResponseJson {
		return (response as TokenErrorJson).error === undefined;
	}

	/**Function to perform request to fetch access and refresh token
	 * @param tokenEndpoint Endpoint to which the token request should be made
	 * @param request tokenRequest object
	 * @param referenceUri Reference uri for the store
	 * @memberof OIDCTokenRequestHandler
	 */
	async performTokenRequest(
		tokenEndpoint: string,
		request: TokenRequest,
		referenceUri: string,
		transactionId: string,
		id?: string
	): Promise<TokenResponse> {
		const Reqmap = request.toStringMap();
		//Remove redirect_uri for refresh flow
		if (request.grantType === GRANT_TYPE_REFRESH_TOKEN) {
			delete Reqmap.redirect_uri;
		}
		const hdrs = DSAuth.getInstance().addBasicHeaders(
			{
				Accept: 'application/json, text/javascript, */*; q=0.01',
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			transactionId
		);
		try {
			const tokenResponse = await DSAuth.getInstance().fetch(
				stringConstants.POST,
				tokenEndpoint,
				hdrs,
				this.utils.stringify(Reqmap),
				referenceUri
			);

			const responseBody = JSON.parse(tokenResponse.body);
			if (this.isTokenResponse(responseBody)) {
				Logger.getInstance().write(
					logConstants.INFO,
					id +
						': Successfully made token request for store ' +
						referenceUri +
						'; grant type: ' +
						request.grantType
				);
				return new TokenResponse(responseBody);
			} else {
				const error = new TokenError(responseBody);
				Logger.getInstance().write(
					logConstants.ERROR,
					id +
						': Error returned during token request for store ' +
						referenceUri +
						' ' +
						error.error +
						' : ' +
						error.errorDescription +
						' : ' +
						error.errorUri +
						' : ' +
						request.grantType
				);
				error.errorDescription += request.grantType;
				throw error;
			}
		} catch (e) {
			if (!(e instanceof HttpRequestError)) {
				if (e instanceof TokenError) {
					SentryLogger.getInstance().addBreadCrumb({
						category: 'OIDCTokenRequest',
						message: `Error returned during token request for store: ${e.error}`,
						level: Sentry.Severity.Info,
					});
				} else {
					SentryLogger.getInstance().logError(e, { tags: { url: referenceUri } });
				}
			}
			Logger.getInstance().write(
				logConstants.ERROR,
				id +
					': Exception making token request for store ' +
					referenceUri +
					' ' +
					e.message +
					' : ' +
					request.grantType
			);
			throw e;
		}
	}

	/**Performs the revocation request and returns the response object
	 * @param revocationEndpoint Endpoint to which the revocation request should be made
	 * @param request request object that contains request details
	 * @param referenceUri Reference Uri for the store
	 * @param id used to keep track of requests from client
	 */
	async performRevokeTokenRequest(
		revocationEndpoint: string,
		request: RevokeTokenRequest,
		referenceUri: string,
		transactionId: string,
		id?: string
	): Promise<void> {
		const hdrs = DSAuth.getInstance().addBasicHeaders(
			{ 'Content-Type': 'application/x-www-form-urlencoded' },
			transactionId
		);

		try {
			const response = await DSAuth.getInstance().fetch(
				stringConstants.POST,
				revocationEndpoint,
				hdrs,
				this.utils.stringify(request.toStringMap()),
				referenceUri
			);

			if (response.statusCode !== statusConstants['200']) {
				Logger.getInstance().write(
					logConstants.ERROR,
					id + ': Revoke Token request failed with ' + response.statusCode
				);
				return;
			}
			Logger.getInstance().write(
				logConstants.INFO,
				id + ': Successfully revoked refresh token for store : ' + referenceUri
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ': Error revoking refresh token for store ' + referenceUri + ' ' + e.message
			);
		}
	}
}
